import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { AuthContext } from "../context/AuthContext";
import {
    PowerStriklePlayer,
    DailyGameResponse,
    PowerStrikleGameState,
    GuessStatus,
    HintDirection,
    getDailyGame,
    submitGuess,
    searchPlayers
} from "../services/PowerStrikleService";
import { divisionMappings } from "../Constants";
import { track } from "../utils/track";
import '../styles/theme.css';
import { KeyboardArrowUp, KeyboardArrowDown, Visibility, VisibilityOff } from "@mui/icons-material";
import PlayerImage from "../components/PlayerImage";
import { useDarkMode } from "../hooks/useDarkmode";
import DivisionSelector from "../components/DivisionSelector/DivisionSelector";
import GamesBackLink from "../components/GamesBackLink";
import GameLeaderboardPanel from "../components/games/GameLeaderboardPanel";

const PowerStriklePage: React.FC = () => {
    const { t } = useTranslation('pages');
    const { user, token } = useContext(AuthContext);
    const [gameData, setGameData] = useState<DailyGameResponse | null>(null);
    const [currentGuess, setCurrentGuess] = useState("");
    const [selectedPlayer, setSelectedPlayer] = useState<PowerStriklePlayer | null>(null);
    const [searchResults, setSearchResults] = useState<PowerStriklePlayer[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState<string>("");
    const isDark = useDarkMode();

    useEffect(() => { track("game-open", { game: "powerstrikle" }); }, []);
    const [selectedDivision, setSelectedDivision] = useState<string>('1div');
    const [hideAnswers, setHideAnswers] = useState(false);
    const [allGameStates, setAllGameStates] = useState<{
        '1div'?: { isComplete: boolean; isWon: boolean; guesses: number };
        '2div'?: { isComplete: boolean; isWon: boolean; guesses: number };
        '3div'?: { isComplete: boolean; isWon: boolean; guesses: number };
        '4div'?: { isComplete: boolean; isWon: boolean; guesses: number };
    }>({});

    // Helper function to convert division names
    const formatDivision = (division: string): string => {
        return divisionMappings.get(division) || division;
    };

    // Load all division states on mount
    useEffect(() => {
        const fetchAllDivisionStates = async () => {
            if (!token) return;
            
            const divisions = ['1div', '2div', '3div', '4div'];
            const states: any = {};
            
            for (const div of divisions) {
                try {
                    const data = await getDailyGame(token, div);
                    if (data) {
                        states[div] = {
                            isComplete: data.gameState.isComplete || false,
                            isWon: data.gameState.isWon || false,
                            guesses: data.gameState.guesses.length || 0
                        };
                    }
                } catch (error) {
                    console.error(`Error fetching ${div} state:`, error);
                }
            }
            
            setAllGameStates(states);
        };
        
        fetchAllDivisionStates();
    }, [token]);

    useEffect(() => {
        const fetchData = async () => {
            if (!token) {
                setLoading(false);
                return;
            }
            
            setLoading(true);
            try {
                const data = await getDailyGame(token, selectedDivision);
                setGameData(data);
                
                // Update the all game states tracker
                setAllGameStates(prev => ({
                    ...prev,
                    [selectedDivision]: {
                        isComplete: data?.gameState.isComplete || false,
                        isWon: data?.gameState.isWon || false,
                        guesses: data?.gameState.guesses.length || 0
                    }
                }));
            } catch (error) {
                console.error("Error fetching game data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [token, selectedDivision]);

    // Countdown timer effect
    useEffect(() => {
        if (!gameData?.secondsUntilNextGame || gameData.secondsUntilNextGame <= 0) return;

        // Calculate the target time when the game was first loaded
        const targetTime = Date.now() + (gameData.secondsUntilNextGame * 1000);

        const updateCountdown = () => {
            const now = Date.now();
            const timeLeft = targetTime - now;

            if (timeLeft <= 0) {
                setTimeRemaining(t('powerstrikle.nextGameReady'));
                return;
            }

            const hours = Math.floor(timeLeft / (1000 * 60 * 60));
            const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

            setTimeRemaining(`${hours}t ${minutes}m ${seconds}s`);
        };

        updateCountdown(); // Initial call
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [gameData]);

    useEffect(() => {
        const searchPlayersByName = async () => {
            if (currentGuess.length >= 2) {
                try {
                    const results = await searchPlayers(currentGuess, selectedDivision, gameData?.activeSeason);
                    setSearchResults(results);
                    setShowSuggestions(true);
                } catch (error) {
                    console.error("Error searching players:", error);
                    setSearchResults([]);
                }
            } else {
                setSearchResults([]);
                setShowSuggestions(false);
            }
        };

        const timeoutId = setTimeout(searchPlayersByName, 300); // Debounce
        return () => clearTimeout(timeoutId);
    }, [currentGuess]);

    const handleSubmitGuess = async () => {
        if (!selectedPlayer || !gameData || submitting || gameData.gameState.isComplete || !token) return;

        setSubmitting(true);
        try {
            const result = await submitGuess(token, selectedPlayer.steamId, selectedDivision);
            if (result) {
                // Update the game data with the new result
                setGameData(prev => prev ? {
                    ...prev,
                    gameState: result.gameState,
                    correctPlayer: result.correctPlayer || prev.correctPlayer
                } : null);
                
                // Update all game states tracker
                setAllGameStates(prev => ({
                    ...prev,
                    [selectedDivision]: {
                        isComplete: result.gameState.isComplete,
                        isWon: result.gameState.isWon,
                        guesses: result.gameState.guesses.length
                    }
                }));
                
                setCurrentGuess("");
                setSelectedPlayer(null);
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error("Error submitting guess:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const handlePlayerSelect = async (player: PowerStriklePlayer) => {
        setCurrentGuess(player.playerName);
        setSelectedPlayer(player);
        setShowSuggestions(false);
        
        // Auto-submit the guess
        if (gameData && !gameData.gameState.isComplete && !submitting && token) {
            setSubmitting(true);
            try {
                const result = await submitGuess(token, player.steamId, selectedDivision);
                if (result) {
                    // Update the game data with the new result
                    setGameData(prev => prev ? {
                        ...prev,
                        gameState: result.gameState,
                        correctPlayer: result.correctPlayer || prev.correctPlayer
                    } : null);
                    
                    // Update all game states tracker
                    setAllGameStates(prev => ({
                        ...prev,
                        [selectedDivision]: {
                            isComplete: result.gameState.isComplete,
                            isWon: result.gameState.isWon,
                            guesses: result.gameState.guesses.length
                        }
                    }));
                    
                    setCurrentGuess("");
                    setSelectedPlayer(null);
                }
            } catch (error) {
                console.error("Error submitting guess:", error);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const getStatusColor = (status: GuessStatus): string => {
        switch (status) {
            case GuessStatus.Correct: 
                return isDark ? 'rgba(0, 128, 0, 0.4)' : 'rgba(135, 245, 135, 0.6)';
            case GuessStatus.Close: 
                return isDark ? 'rgba(204, 204, 0, 0.4)' : 'rgba(250, 250, 178, 0.8)';
            case GuessStatus.SameDivision: 
                return isDark ? 'rgba(0, 123, 255, 0.4)' : 'rgba(173, 216, 230, 0.8)';
            case GuessStatus.Wrong: 
                return isDark ? 'rgba(139, 0, 0, 0.4)' : 'rgba(250, 169, 182, 0.6)';
            default: 
                return 'transparent';
        }
    };

    // The server sends only which way the answer lies ("up"/"down"), never its value.
    const getDirectionalArrow = (hint: HintDirection | null | undefined, status: GuessStatus) => {
        if (status === GuessStatus.Correct || !hint) return null;
        return hint === "up"
            ? <KeyboardArrowUp className="ms-1" style={{ fontSize: '16px' }} />
            : <KeyboardArrowDown className="ms-1" style={{ fontSize: '16px' }} />;
    };

    if (!user || !token) {
        return (
            <div className="container-fluid" style={{ 
                maxWidth: '900px', 
                padding: '1rem',
                minHeight: '100vh',
                background: isDark ? '#1a1a1a' : '#f8f9fa'
            }}>
                <div className="text-center mb-3" style={{ padding: '2rem 0' }}>
                    <h1 style={{ 
                        fontSize: 'clamp(1.9rem, 11vw, 3.5rem)',
                        fontWeight: '800',
                        letterSpacing: '0.02em',
                        color: isDark ? '#ffffff' : '#2c3e50',
                        marginBottom: '0.5rem',
                        textShadow: isDark ? '0 2px 4px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        POWER<span style={{ fontStyle: 'italic', color: '#e74c3c' }}>STRIKLE</span>
                    </h1>
                    <p style={{
                        fontSize: '1.2rem',
                        color: isDark ? '#a0a0a0' : '#7f8c8d',
                        marginBottom: '2rem'
                    }}>
                        {t('powerstrikle.subtitle')}
                    </p>
                    <div style={{
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'white',
                        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6'}`,
                        borderRadius: '12px',
                        padding: '2rem',
                        maxWidth: '500px',
                        margin: '0 auto'
                    }}>
                        <h3 style={{ marginBottom: '1rem' }}>{t('powerstrikle.loginTitle')}</h3>
                        <p style={{ color: isDark ? '#a0a0a0' : '#7f8c8d' }}>
                            {t('powerstrikle.loginDesc')}
                        </p>
                        <p style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
                            {t('powerstrikle.loginInfo')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (loading && !gameData) {
        return (
            <div className="container text-center">
                <h1>PowerStrikle</h1>
                <p>{t('powerstrikle.loading')}</p>
            </div>
        );
    }

    if (!gameData) {
        return (
            <div className="container text-center">
                <h1>PowerStrikle</h1>
                <p>{t('powerstrikle.noGame')}</p>
            </div>
        );
    }

    const { gameState, correctPlayer, nextGameDate, secondsUntilNextGame } = gameData;

    return (
        <div className="container-fluid" style={{
            maxWidth: '900px',
            padding: '1rem 1rem 96px',
            minHeight: '100vh',
            background: isDark ? '#1a1a1a' : '#f8f9fa'
        }}>
            <GamesBackLink />

            {/* Header */}
            <div className="text-center mb-3" style={{ padding: '2rem 0' }}>
                <h1 style={{
                    fontSize: 'clamp(1.9rem, 11vw, 3.5rem)',
                    fontWeight: '800',
                    letterSpacing: '0.02em',
                    color: isDark ? '#ffffff' : '#2c3e50',
                    marginBottom: '0.5rem',
                    textShadow: isDark ? '0 2px 4px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    POWER<span style={{ fontStyle: 'italic', color: '#e74c3c' }}>STRIKLE</span>
                </h1>
                <div style={{
                    fontSize: '0.9rem',
                    color: '#e74c3c',
                    fontWeight: '600',
                    marginBottom: '0.5rem'
                }}>
                    {selectedDivision === '1div' && '1. Division'}
                    {selectedDivision === '2div' && '2. Division'}
                    {selectedDivision === '3div' && '3. Division'}
                    {selectedDivision === '4div' && '4. Division'}
                </div>
                <p style={{
                    fontSize: '1.1rem',
                    color: isDark ? '#b8b8b8' : '#7f8c8d',
                    fontWeight: '500',
                    margin: '0'
                }}>
                    {t('powerstrikle.guessToday', { season: gameData?.activeSeason || 29 })}
                </p>
            </div>

            {/* Game Status */}
            {gameState.isComplete && (
                <div className="text-center mb-4" style={{
                    backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                    border: isDark ? '1px solid #404040' : '1px solid #e9ecef'
                }}>
                    {gameState.isWon ? (
                        <div className="mb-3">
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
                            <h3 style={{
                                color: '#27ae60',
                                fontWeight: '700',
                                marginBottom: '0.5rem'
                            }}>{t('powerstrikle.congratulations')}</h3>
                            <p style={{
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                fontSize: '1.1rem'
                            }}>
                                {t('powerstrikle.guessedIn', { count: gameState.guesses.length })}
                            </p>
                        </div>
                    ) : (
                        <div className="mb-3">
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>😞</div>
                            <h3 style={{
                                color: '#e74c3c',
                                fontWeight: '700',
                                marginBottom: '0.5rem'
                            }}>{t('powerstrikle.gameOver')}</h3>
                            <p style={{
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                fontSize: '1.1rem'
                            }}>
                                {t('powerstrikle.usedAllAttempts', { count: gameState.maxGuesses })}
                            </p>
                        </div>
                    )}
                    
                    {correctPlayer && !hideAnswers && (
                        <div className="mb-4">
                            <h5 style={{
                                color: isDark ? '#ffffff' : '#2c3e50',
                                fontWeight: '600',
                                marginBottom: '1.5rem'
                            }}>
                                {gameState.isWon ? t('powerstrikle.youGuessedCorrect') : t('powerstrikle.correctPlayerWas')}
                            </h5>
                            <Link 
                                to={`/player/${correctPlayer.steamId}`}
                                className="d-flex justify-content-center align-items-center gap-4 text-decoration-none flex-wrap"
                                style={{
                                    backgroundColor: isDark ? '#3a3a3a' : '#f8f9fa',
                                    borderRadius: '8px',
                                    padding: '1.5rem',
                                    border: isDark ? '1px solid #505050' : '1px solid #dee2e6',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease'
                                }}
                                onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                    e.currentTarget.style.backgroundColor = isDark ? '#454545' : '#e9ecef';
                                }}
                                onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                    e.currentTarget.style.backgroundColor = isDark ? '#3a3a3a' : '#f8f9fa';
                                }}
                            >
                                <PlayerImage id={correctPlayer.steamId} width={90} />
                                <div className="text-start">
                                    <h4 style={{ 
                                        color: isDark ? '#ffffff' : '#2c3e50',
                                        fontWeight: '700',
                                        marginBottom: '1rem'
                                    }}>
                                        {correctPlayer.playerName}
                                    </h4>
                                    <div style={{ color: isDark ? '#b8b8b8' : '#7f8c8d' }}>
                                        <div style={{ marginBottom: '0.25rem' }}>
                                            <strong>{t('powerstrikle.team')}:</strong> {correctPlayer.currentTeam}
                                        </div>
                                        <div style={{ marginBottom: '0.25rem' }}>
                                            <strong>{t('powerstrikle.age')}:</strong> {correctPlayer.age} {t('powerstrikle.years')}
                                        </div>
                                        <div style={{ marginBottom: '0.25rem' }}>
                                            <strong>{t('powerstrikle.seasons')}:</strong> {correctPlayer.seasonsPlayed}
                                        </div>
                                        <div>
                                            <strong>{t('powerstrikle.highestDivision')}:</strong> {formatDivision(correctPlayer.highestDivision)}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    )}

                    <div style={{
                        color: isDark ? '#b8b8b8' : '#7f8c8d',
                        fontSize: '0.95rem',
                        fontWeight: '500'
                    }}>
                        {t('powerstrikle.nextGame', { date: nextGameDate, time: timeRemaining })}
                    </div>
                </div>
            )}

            {/* Input Section */}
            {!gameState.isComplete && (
                <div className="text-center mb-4">
                    <div className="d-inline-block position-relative" style={{ width: '100%', maxWidth: '500px' }}>
                        <input
                            type="text"
                            placeholder={t('powerstrikle.searchPlayer')}
                            value={currentGuess}
                            onChange={(e) => setCurrentGuess(e.target.value)}
                            disabled={submitting}
                            style={{
                                width: '100%',
                                padding: '1rem 1.5rem',
                                fontSize: '1.1rem',
                                border: isDark ? '2px solid #505050' : '2px solid #dee2e6',
                                borderRadius: '12px',
                                backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
                                color: isDark ? '#ffffff' : '#2c3e50',
                                outline: 'none',
                                transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#e74c3c';
                                e.target.style.boxShadow = `0 0 0 3px ${isDark ? 'rgba(231, 76, 60, 0.2)' : 'rgba(231, 76, 60, 0.1)'}`;
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = isDark ? '#505050' : '#dee2e6';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        {submitting && (
                            <div style={{
                                marginTop: '0.75rem',
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                fontSize: '0.95rem'
                            }}>
                                {t('powerstrikle.submitting')}
                            </div>
                        )}
                        
                        {/* Player Suggestions */}
                        {showSuggestions && searchResults.length > 0 && (
                            <div style={{
                                position: 'absolute',
                                width: '100%',
                                top: '100%',
                                left: '0',
                                backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
                                border: isDark ? '1px solid #505050' : '1px solid #dee2e6',
                                borderRadius: '8px',
                                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                                zIndex: 1000,
                                marginTop: '0.5rem',
                                overflow: 'hidden'
                            }}>
                                {searchResults.map((player, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handlePlayerSelect(player)}
                                        disabled={submitting}
                                        style={{
                                            width: '100%',
                                            padding: '1rem 1.5rem',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            color: isDark ? '#ffffff' : '#2c3e50',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.15s ease',
                                            borderBottom: index < searchResults.length - 1 ? 
                                                (isDark ? '1px solid #404040' : '1px solid #f1f3f4') : 'none'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = isDark ? '#3a3a3a' : '#f8f9fa';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                    >
                                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                            {player.playerName}
                                        </div>
                                        <div style={{ 
                                            fontSize: '0.9rem',
                                            color: isDark ? '#b8b8b8' : '#7f8c8d'
                                        }}>
                                            {player.currentTeam} • {formatDivision(player.highestDivision)}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Guesses Grid */}
            <div className="d-flex justify-content-center mb-4">
                <div style={{ 
                    width: '100%', 
                    maxWidth: '900px',
                    position: 'relative'
                }}>
                    {/* Hide/Show Answers Button - Small and positioned top-right */}
                    {gameState.guesses.length > 0 && (
                        <button
                            onClick={() => setHideAnswers(!hideAnswers)}
                            style={{
                                position: 'absolute',
                                top: '-35px',
                                right: '0',
                                backgroundColor: 'transparent',
                                border: `1px solid ${isDark ? '#505050' : '#dee2e6'}`,
                                borderRadius: '6px',
                                padding: '4px 8px',
                                fontSize: '0.8rem',
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                                zIndex: 10
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = isDark ? '#3a3a3a' : '#f8f9fa';
                                e.currentTarget.style.borderColor = '#e74c3c';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.borderColor = isDark ? '#505050' : '#dee2e6';
                            }}
                            title={hideAnswers ? t('powerstrikle.show') : t('powerstrikle.hide')}
                        >
                            {hideAnswers ? <Visibility style={{ fontSize: '14px' }} /> : <VisibilityOff style={{ fontSize: '14px' }} />}
                            <span>{hideAnswers ? t('powerstrikle.show') : t('powerstrikle.hide')}</span>
                        </button>
                    )}
                    
                    <div style={{ 
                        backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                        border: isDark ? '1px solid #404040' : '1px solid #e9ecef'
                    }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                minWidth: '520px',
                                borderCollapse: 'collapse',
                                margin: 0
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: '#e74c3c',
                                        color: '#ffffff'
                                    }}>
                                        <th style={{
                                        padding: '1rem 0.75rem',
                                        textAlign: 'center',
                                        fontWeight: '700',
                                        fontSize: '0.9rem',
                                        width: '8%',
                                        borderRight: '1px solid rgba(255,255,255,0.1)'
                                    }}>#</th>
                                    <th style={{
                                        padding: '1rem 0.75rem',
                                        textAlign: 'center',
                                        fontWeight: '700',
                                        fontSize: '0.9rem',
                                        width: '20%',
                                        borderRight: '1px solid rgba(255,255,255,0.1)'
                                    }}>{t('powerstrikle.name')}</th>
                                    <th style={{
                                        padding: '1rem 0.75rem',
                                        textAlign: 'center',
                                        fontWeight: '700',
                                        fontSize: '0.9rem',
                                        width: '20%',
                                        borderRight: '1px solid rgba(255,255,255,0.1)'
                                    }}>{t('powerstrikle.tableTeam')}</th>
                                    <th style={{
                                        padding: '1rem 0.75rem',
                                        textAlign: 'center',
                                        fontWeight: '700',
                                        fontSize: '0.9rem',
                                        width: '15%',
                                        borderRight: '1px solid rgba(255,255,255,0.1)'
                                    }}>{t('powerstrikle.tableAge')}</th>
                                    <th style={{
                                        padding: '1rem 0.75rem',
                                        textAlign: 'center',
                                        fontWeight: '700',
                                        fontSize: '0.9rem',
                                        width: '15%',
                                        borderRight: '1px solid rgba(255,255,255,0.1)'
                                    }}>{t('powerstrikle.tableSeasons')}</th>
                                    <th style={{
                                        padding: '1rem 0.75rem',
                                        textAlign: 'center',
                                        fontWeight: '700',
                                        fontSize: '0.9rem',
                                        width: '22%'
                                    }}>{t('powerstrikle.tableHighestDiv')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {gameState.guesses.map((guessResult, index) => (
                                    <tr key={index}>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            fontWeight: '700',
                                            color: isDark ? '#ffffff' : '#2c3e50',
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)'
                                        }}>
                                            {guessResult.guessNumber}
                                        </td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            fontWeight: '600',
                                            color: isDark ? '#ffffff' : '#2c3e50',
                                            backgroundColor: getStatusColor(guessResult.comparison.playerName),
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)'
                                        }}>
                                            {hideAnswers ? '???' : guessResult.guess.playerName}
                                        </td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            fontWeight: '500',
                                            color: isDark ? '#ffffff' : '#2c3e50',
                                            backgroundColor: getStatusColor(guessResult.comparison.team),
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)'
                                        }}>
                                            {hideAnswers ? '???' : guessResult.guess.team}
                                        </td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            fontWeight: '500',
                                            color: isDark ? '#ffffff' : '#2c3e50',
                                            backgroundColor: getStatusColor(guessResult.comparison.age),
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)'
                                        }}>
                                            <div className="d-flex align-items-center justify-content-center">
                                                <span>{hideAnswers ? '?' : guessResult.guess.age}</span>
                                                {!hideAnswers && getDirectionalArrow(guessResult.comparison.ageHint, guessResult.comparison.age)}
                                            </div>
                                        </td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            fontWeight: '500',
                                            color: isDark ? '#ffffff' : '#2c3e50',
                                            backgroundColor: getStatusColor(guessResult.comparison.seasonsPlayed),
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)'
                                        }}>
                                            <div className="d-flex align-items-center justify-content-center">
                                                <span>{hideAnswers ? '?' : guessResult.guess.seasonsPlayed}</span>
                                                {!hideAnswers && getDirectionalArrow(guessResult.comparison.seasonsPlayedHint, guessResult.comparison.seasonsPlayed)}
                                            </div>
                                        </td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            fontWeight: '500',
                                            color: isDark ? '#ffffff' : '#2c3e50',
                                            backgroundColor: getStatusColor(guessResult.comparison.highestDivision),
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4'
                                        }}>
                                            <div className="d-flex align-items-center justify-content-center">
                                                <span>{hideAnswers ? '???' : formatDivision(guessResult.guess.highestDivision)}</span>
                                                {!hideAnswers && getDirectionalArrow(guessResult.comparison.highestDivisionHint, guessResult.comparison.highestDivision)}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                
                                {/* Empty rows for remaining guesses */}
                                {Array.from({ length: gameState.maxGuesses - gameState.guesses.length }, (_, index) => (
                                    <tr key={`empty-${index}`}>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            color: isDark ? '#666666' : '#9ca3af',
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)',
                                            fontWeight: '500'
                                        }}>
                                            {gameState.guesses.length + index + 1}
                                        </td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            color: isDark ? '#666666' : '#9ca3af',
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)'
                                        }}>-</td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            color: isDark ? '#666666' : '#9ca3af',
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)'
                                        }}>-</td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            color: isDark ? '#666666' : '#9ca3af',
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)'
                                        }}>-</td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            color: isDark ? '#666666' : '#9ca3af',
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4',
                                            borderRight: '1px solid rgba(0,0,0,0.05)'
                                        }}>-</td>
                                        <td style={{ 
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            color: isDark ? '#666666' : '#9ca3af',
                                            borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f3f4'
                                        }}>-</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                </div>
            </div>

            {/* Legend */}
            <div className="d-flex justify-content-center">
                <div style={{
                    backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                    border: isDark ? '1px solid #404040' : '1px solid #e9ecef',
                    maxWidth: '700px',
                    width: '100%'
                }}>
                    <h6 style={{
                        color: isDark ? '#ffffff' : '#2c3e50',
                        fontWeight: '700',
                        textAlign: 'center',
                        marginBottom: '1.5rem',
                        fontSize: '1.1rem'
                    }}>
                        {t('powerstrikle.howToPlay')}
                    </h6>
                    <div className="d-flex justify-content-center gap-4 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                            <div style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#27ae60',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <span style={{ color: '#ffffff', fontSize: '12px', fontWeight: '700' }}>✓</span>
                            </div>
                            <span style={{
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                fontSize: '0.95rem',
                                fontWeight: '500'
                            }}>{t('powerstrikle.correct')}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <div style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#f39c12',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <span style={{ color: '#ffffff', fontSize: '12px', fontWeight: '700' }}>~</span>
                            </div>
                            <span style={{
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                fontSize: '0.95rem',
                                fontWeight: '500'
                            }}>{t('powerstrikle.close')}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <div style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#007bff',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <span style={{ color: '#ffffff', fontSize: '12px', fontWeight: '700' }}>≈</span>
                            </div>
                            <span style={{
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                fontSize: '0.95rem',
                                fontWeight: '500'
                            }}>{t('powerstrikle.wrongGroup')}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <div style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#e74c3c',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <span style={{ color: '#ffffff', fontSize: '12px', fontWeight: '700' }}>✗</span>
                            </div>
                            <span style={{
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                fontSize: '0.95rem',
                                fontWeight: '500'
                            }}>{t('powerstrikle.wrong')}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <KeyboardArrowUp style={{
                                fontSize: '20px',
                                color: isDark ? '#b8b8b8' : '#7f8c8d'
                            }} />
                            <span style={{
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                fontSize: '0.95rem',
                                fontWeight: '500'
                            }}>{t('powerstrikle.higher')}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <KeyboardArrowDown style={{
                                fontSize: '20px',
                                color: isDark ? '#b8b8b8' : '#7f8c8d'
                            }} />
                            <span style={{
                                color: isDark ? '#b8b8b8' : '#7f8c8d',
                                fontSize: '0.95rem',
                                fontWeight: '500'
                            }}>{t('powerstrikle.lower')}</span>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Division Selector */}
            <DivisionSelector 
                selectedDivision={selectedDivision}
                onDivisionChange={setSelectedDivision}
                isDark={isDark}
                gameStates={allGameStates}
            />

            <GameLeaderboardPanel game="powerstrikle" variant="all" refreshKey={gameData?.gameState?.guesses?.length ?? 0} />
        </div>
    );
};

export default PowerStriklePage;